import React, { useState } from "react";
import axios from "axios";
import { Logo } from "./Logo";
import { ProgressBar } from "./ProgressBar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DayPage = ({ day, content, email, onAdvance, onComplete }) => {
  const [answer, setAnswer] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const isFinal = day === 5;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!answer.trim()) {
      setError("Take a moment — write something honest. It's just for you.");
      return;
    }
    setError("");
    setLoading(true);
    try {
      if (isFinal) {
        // Mark challenge complete; answers never leave the browser
        await axios.post(`${API}/challenge/complete`, { email });
        onComplete();
      } else {
        await axios.post(`${API}/challenge/advance`, { email });
        onAdvance();
      }
    } catch (err) {
      setError("Something went wrong. Please try again in a moment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-cream py-16 sm:py-24 px-6 sm:px-8 animate-glow-fade-up"
      data-testid={`day-page-${day}`}
    >
      <div className="absolute top-6 left-6 md:top-10 md:left-12 z-20">
        <Logo />
      </div>

      <div className="max-w-[680px] mx-auto flex flex-col gap-12 pt-10 md:pt-16">
        <div className="flex flex-col gap-5 items-start">
          <ProgressBar currentDay={day} />
          <p
            className="font-body text-xs uppercase tracking-[0.32em] text-gold"
            data-testid={`day-label-${day}`}
          >
            Day {day} of 5 — {content.theme}
          </p>
          <h1
            className="font-serif-editorial text-espresso leading-[1.02] tracking-tight text-4xl sm:text-5xl lg:text-6xl"
            data-testid={`day-headline-${day}`}
          >
            {content.headline}
          </h1>
        </div>

        {/* Content block: cream card with gold left border */}
        <div
          className="bg-cream border-l-2 border-gold pl-6 sm:pl-8 py-2 flex flex-col gap-5"
          data-testid={`day-content-${day}`}
        >
          {content.body.map((para, i) => (
            <p
              key={i}
              className="font-body text-[17px] leading-[1.75] text-espresso/85"
            >
              {para}
            </p>
          ))}
        </div>

        {/* Reflection prompt: gold filled card */}
        <div
          className="bg-gold px-8 py-10 sm:px-10 sm:py-12 flex flex-col gap-3 shadow-sm"
          data-testid={`day-prompt-${day}`}
        >
          <p className="font-body text-xs uppercase tracking-[0.32em] text-espresso/80">
            ✦ Today's Glow Up Prompt
          </p>
          <p className="font-serif-editorial italic text-xl sm:text-2xl leading-snug text-espresso">
            {content.prompt}
          </p>
        </div>

        {/* Check-in form */}
        <form
          onSubmit={handleSubmit}
          className="flex flex-col gap-5"
          data-testid={`day-checkin-form-${day}`}
        >
          <label className="font-body text-xs uppercase tracking-[0.24em] text-espresso/70">
            {content.inputLabel}
          </label>
          <textarea
            className="textarea-soft font-body"
            value={answer}
            onChange={(e) => setAnswer(e.target.value)}
            placeholder={content.placeholder}
            data-testid={`day-answer-${day}`}
            rows={6}
          />

          {error ? (
            <p
              className="font-body text-sm text-espresso/80 border-l-2 border-gold pl-3"
              data-testid={`day-error-${day}`}
            >
              {error}
            </p>
          ) : null}

          <div className="flex flex-col gap-3 pt-2">
            <button
              type="submit"
              className="btn-gold w-full sm:w-auto self-start"
              disabled={loading}
              data-testid={`day-submit-${day}`}
            >
              {loading
                ? "Saving your moment…"
                : `${content.buttonLabel} →`}
            </button>
            <p
              className="font-body text-xs text-espresso/55 leading-relaxed max-w-md"
              data-testid={`day-fineprint-${day}`}
            >
              {content.fineprint} Your answer stays in your browser only — it is
              not shared or stored on our servers.
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};
